import React, { FC, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Breadcrumb, List, Empty, Spin, Button, Typography, Space } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import IconFolder from "@/assets/images/fileIcon/folder.svg";

import { Doc, DocTypeEnum } from "@/types/doc";

const { Text } = Typography;

interface MoveModalProps {
  visible: boolean;
  onClose: () => void;
  selectedDoc: Doc | null;
  rootDoc: Doc | null;
  onMove: (targetFolderId: string) => Promise<void>;
}

const MoveModal: FC<MoveModalProps> = ({
  visible,
  onClose,
  selectedDoc,
  rootDoc,
  onMove,
}) => {
  const { t } = useTranslation();

  const [currentFolderId, setCurrentFolderId] = useState<string>("");
  const [pathStack, setPathStack] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [moving, setMoving] = useState<boolean>(false);

  // 当前显示的文件夹列表（只显示文件夹，不显示文件）
  const folderList = useMemo(() => {
    if (!rootDoc) return [];

    // 获取当前文件夹
    const getCurrentFolder = (node: Doc, folderId: string): Doc | null => {
      if (!folderId || node.id === folderId) return node;
      if (node.subs) {
        for (const sub of node.subs) {
          const found = getCurrentFolder(sub, folderId);
          if (found) return found;
        }
      }
      return null;
    };

    const currentFolder = getCurrentFolder(rootDoc, currentFolderId);
    const subs = currentFolder?.subs || [];

    // 只返回文件夹类型的项，排除自身
    return subs.filter(
      (sub) => sub.nodeType === DocTypeEnum.NODE && sub.id !== selectedDoc?.id
    );
  }, [rootDoc, currentFolderId, selectedDoc]);

  // 初始化：打开弹窗时设置当前位置为根目录
  const handleOpen = useCallback(() => {
    if (rootDoc) {
      setCurrentFolderId(rootDoc.id);
      setPathStack([{ id: rootDoc.id, name: rootDoc.name || t('components.rootDirectory') }]);
    }
  }, [rootDoc, t]);

  // 进入文件夹
  const handleEnterFolder = useCallback((folder: Doc) => {
    setCurrentFolderId(folder.id);
    setPathStack((prev) => [...prev, { id: folder.id, name: folder.name }]);
  }, []);

  // 点击面包屑导航
  const handleBreadcrumbClick = useCallback((index: number) => {
    setPathStack((prev) => prev.slice(0, index + 1));
    const target = pathStack[index];
    if (target) {
      setCurrentFolderId(target.id);
    }
  }, [pathStack]);

  // 执行移动
  const handleMove = useCallback(async () => {
    if (!selectedDoc) return;
    setMoving(true);
    try {
      await onMove(currentFolderId);
      onClose();
    } finally {
      setMoving(false);
    }
  }, [selectedDoc, currentFolderId, onMove, onClose]);

  // 重置状态
  const handleClose = useCallback(() => {
    setCurrentFolderId("");
    setPathStack([]);
    onClose();
  }, [onClose]);

  // 监听 visible 变化
  React.useEffect(() => {
    if (visible) {
      handleOpen();
    }
  }, [visible, handleOpen]);

  // 获取当前文件夹名称
  const currentFolderName = useMemo(() => {
    if (pathStack.length === 0) return "";
    if (pathStack.length === 1) return t('components.rootDirectory');
    return pathStack[pathStack.length - 1]?.name || t('components.rootDirectory');
  }, [pathStack, t]);

  // 检查是否移动到自己（禁用移动按钮）
  const isMoveToSelf = useMemo(() => {
    return currentFolderId === selectedDoc?.id;
  }, [currentFolderId, selectedDoc]);

  // 面包屑导航点击根目录
  const handleRootClick = useCallback(() => {
    if (rootDoc) {
      setCurrentFolderId(rootDoc.id);
      setPathStack([{ id: rootDoc.id, name: rootDoc.name || t('components.rootDirectory') }]);
    }
  }, [rootDoc, t]);

  return (
    <Modal
      title={t('components.moveTitle')}
      open={visible}
      onCancel={handleClose}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Text type="secondary">
            {selectedDoc && t('components.moving', { name: selectedDoc.name })}
          </Text>
          <Space>
            <Button onClick={handleClose}>{t('components.cancel')}</Button>
            <Button
              type="primary"
              onClick={handleMove}
              loading={moving}
              disabled={isMoveToSelf}
            >
              {t('components.moveToHere')}
            </Button>
          </Space>
        </div>
      }
    >
      <Spin spinning={loading}>
        <div style={{ minHeight: 300 }}>
          {/* 面包屑导航 */}
          <Breadcrumb
            style={{ marginBottom: 16 }}
            items={[
              {
                title: (
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={handleRootClick}
                  >
                    <HomeOutlined />
                  </span>
                ),
              },
              ...pathStack.slice(1).map((item, index) => ({
                title: (
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={() => handleBreadcrumbClick(index)}
                  >
                    {item.name}
                  </span>
                ),
              })),
            ]}
          />

          {/* 当前位置 */}
          <div style={{ marginBottom: 8 }}>
            <Text strong>{t('components.currentLocation')}: {currentFolderName}</Text>
          </div>

          {/* 文件夹列表 */}
          <List
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              minHeight: 200,
            }}
            dataSource={folderList}
            renderItem={(item) => (
              <List.Item
                style={{
                  cursor: "pointer",
                  padding: "8px 16px",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                onClick={() => handleEnterFolder(item)}
              >
                <List.Item.Meta
                  avatar={<img src={IconFolder} alt="" style={{ width: 24, height: 24 }} />}
                  title={item.name}
                />
              </List.Item>
            )}
            locale={{ emptyText: <Empty description={t('components.noFolders')} /> }}
          />
        </div>
      </Spin>
    </Modal>
  );
};

export default MoveModal;
